import { useEffect, useRef, useState } from 'react';
import type { TeamSummary } from '@mockd/shared';
import { Alert } from '../common/Alert';
import { Modal } from '../common/Modal';

interface CreateTeamModalProps {
  isOpen: boolean;
  onCreate: (name: string) => Promise<TeamSummary>;
  onCreated: (team: TeamSummary) => void;
  onClose: () => void;
}

export function CreateTeamModal({ isOpen, onCreate, onCreated, onClose }: CreateTeamModalProps) {
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setName('');
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Team name is required');
      return;
    }

    setIsCreating(true);
    try {
      const team = await onCreate(name.trim());
      onCreated(team);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create team');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create team" initialFocusRef={nameInputRef} showCloseButton>
      <form onSubmit={handleSubmit} className="space-y-4 mt-4">
        {error && <Alert variant="error">{error}</Alert>}

        <div className="fieldset">
          <label htmlFor="team-name" className="fieldset-legend">
            Team name
          </label>
          <input
            ref={nameInputRef}
            id="team-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Acme Inc"
            className="input w-full"
            disabled={isCreating}
          />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn btn-ghost" disabled={isCreating}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={isCreating}>
            {isCreating ? 'Creating…' : 'Create team'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
